import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '@stores/gameStore';

interface PlaneProps {
  startPosition?: [number, number, number];
  speed?: number;
}

// Aviation color palette
const PLANE_COLORS = {
  fuselage: '#dc2626', // Red
  fuselageAccent: '#b91c1c', // Darker red
  cockpit: '#60a5fa', // Sky blue glass
  wing: '#991b1b', // Dark red
  tail: '#7f1d1d', // Even darker red
  engine: '#374151', // Gray
  propeller: '#1f2937', // Dark gray
  stripe: '#ffffff', // White accent stripe
};


/**
 * Engine exhaust trail particles
 */
function ExhaustTrail({ position }: { position: THREE.Vector3 }): React.JSX.Element {
  const particlesRef = useRef<THREE.Points>(null);
  const particleCount = 50;

  const particles = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);
    const opacities = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = -i * 0.3; // Trail behind plane
      positions[i * 3 + 1] = (Math.random() - 0.5) * 0.2;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 0.2;
      sizes[i] = Math.max(0.1, 0.3 - i * 0.005);
      opacities[i] = Math.max(0, 1 - i / particleCount);
    }

    return { positions, sizes, opacities };
  }, []);

  useFrame((state) => {
    if (particlesRef.current) {
      const positions = particlesRef.current.geometry.attributes['position']?.array as Float32Array | undefined;
      if (positions) {
        const time = state.clock.elapsedTime;
        for (let i = 0; i < particleCount; i++) {
          positions[i * 3 + 1] = (Math.sin(time * 10 + i) * 0.1) * (1 - i / particleCount);
          positions[i * 3 + 2] = (Math.cos(time * 10 + i) * 0.1) * (1 - i / particleCount);
        }
        particlesRef.current.geometry.attributes['position']!.needsUpdate = true;
      }
    }
  });

  return (
    <points ref={particlesRef} position={position.toArray()}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles.positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.15}
        color="#f97316"
        transparent
        opacity={0.6}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

/**
 * Plane wing component
 */
function Wing({
  position,
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
}: {
  position: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
}): React.JSX.Element {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Main wing body */}
      <mesh castShadow>
        <boxGeometry args={[2.5, 0.08, 0.5]} />
        <meshStandardMaterial color={PLANE_COLORS.wing} roughness={0.4} metalness={0.2} />
      </mesh>
      {/* Wing tip */}
      <mesh position={[1.3, 0, 0]} castShadow>
        <boxGeometry args={[0.2, 0.06, 0.3]} />
        <meshStandardMaterial color={PLANE_COLORS.fuselageAccent} roughness={0.4} metalness={0.2} />
      </mesh>
      {/* Wing light (red/green) */}
      <mesh position={[1.35, 0.05, 0]}>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial
          color={scale[0] > 0 ? '#22c55e' : '#ef4444'}
          emissive={scale[0] > 0 ? '#22c55e' : '#ef4444'}
          emissiveIntensity={0.8}
        />
      </mesh>
    </group>
  );
}

/**
 * Plane tail component
 */
function Tail({ position }: { position: [number, number, number] }): React.JSX.Element {
  return (
    <group position={position}>
      {/* Vertical stabilizer */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[0.4, 0.8, 0.08]} />
        <meshStandardMaterial color={PLANE_COLORS.tail} roughness={0.4} metalness={0.2} />
      </mesh>
      {/* Rudder */}
      <mesh position={[-0.15, 0.4, 0]} castShadow>
        <boxGeometry args={[0.15, 0.7, 0.06]} />
        <meshStandardMaterial color={PLANE_COLORS.fuselageAccent} roughness={0.4} metalness={0.2} />
      </mesh>
      {/* Horizontal stabilizer */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <boxGeometry args={[0.4, 0.06, 1.2]} />
        <meshStandardMaterial color={PLANE_COLORS.tail} roughness={0.4} metalness={0.2} />
      </mesh>
      {/* Elevators */}
      <mesh position={[-0.15, 0.1, 0.4]} castShadow>
        <boxGeometry args={[0.15, 0.05, 0.4]} />
        <meshStandardMaterial color={PLANE_COLORS.fuselageAccent} roughness={0.4} metalness={0.2} />
      </mesh>
      <mesh position={[-0.15, 0.1, -0.4]} castShadow>
        <boxGeometry args={[0.15, 0.05, 0.4]} />
        <meshStandardMaterial color={PLANE_COLORS.fuselageAccent} roughness={0.4} metalness={0.2} />
      </mesh>
    </group>
  );
}

/**
 * Propeller component with animated spinning
 */
function Propeller({ position }: { position: [number, number, number] }): React.JSX.Element {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      // Fast rotation for propeller
      groupRef.current.rotation.x = state.clock.elapsedTime * 50;
    }
  });

  return (
    <group ref={groupRef} position={position}>
      {/* Propeller hub */}
      <mesh castShadow>
        <cylinderGeometry args={[0.08, 0.06, 0.1, 12]} />
        <meshStandardMaterial color={PLANE_COLORS.propeller} roughness={0.3} metalness={0.8} />
      </mesh>
      {/* Propeller blades */}
      <mesh rotation={[0, 0, 0]} castShadow>
        <boxGeometry args={[0.08, 0.8, 0.02]} />
        <meshStandardMaterial color={PLANE_COLORS.propeller} roughness={0.4} metalness={0.6} />
      </mesh>
      <mesh rotation={[0, 0, Math.PI / 2]} castShadow>
        <boxGeometry args={[0.08, 0.8, 0.02]} />
        <meshStandardMaterial color={PLANE_COLORS.propeller} roughness={0.4} metalness={0.6} />
      </mesh>
    </group>
  );
}

/**
 * Main aircraft component
 * Features: fuselage, wings, tail, cockpit, engine, propeller, exhaust trail
 */
export function Plane({ startPosition = [-10, 2, 0], speed = 1 }: PlaneProps): React.JSX.Element {
  const groupRef = useRef<THREE.Group>(null);
  const exhaustPositionRef = useRef(new THREE.Vector3());

  const { multiplier, gamePhase } = useGameStore();

  // Calculate plane position based on multiplier
  // Plane moves right and up as multiplier increases (takeoff trajectory)
  const targetPosition = useMemo(() => {
    const progress = Math.min((multiplier - 1) / 9, 1);
    const x = -10 + progress * 25; // Move from -10 to 15
    const y = 2 + progress * 15; // Climb from 2 to 17
    return { x, y };
  }, [multiplier]);

  // Calculate climb angle based on multiplier
  const climbAngle = useMemo(() => {
    // Start with steeper angle, gradually level off
    const baseAngle = Math.PI / 6; // 30 degrees
    const progress = Math.min((multiplier - 1) / 5, 1);
    return baseAngle * (1 - progress * 0.5); // Reduce angle as we climb
  }, [multiplier]);

  // Animation loop
  useFrame((state) => {
    if (!groupRef.current) return;

    const time = state.clock.elapsedTime;

    // Only animate when playing
    if (gamePhase === 'playing') {
      // Smooth movement toward target position
      groupRef.current.position.x = THREE.MathUtils.lerp(
        groupRef.current.position.x,
        targetPosition.x,
        0.05 * speed
      );
      groupRef.current.position.y = THREE.MathUtils.lerp(
        groupRef.current.position.y,
        targetPosition.y,
        0.05 * speed
      );

      // Set climb angle (nose up)
      groupRef.current.rotation.z = -climbAngle;

      // Subtle banking motion
      groupRef.current.rotation.x = Math.sin(time * 0.5) * 0.02;
    } else if (gamePhase === 'betting' || gamePhase === 'waiting') {
      // Reset to starting position
      groupRef.current.position.x = startPosition[0];
      groupRef.current.position.y = startPosition[1];
      groupRef.current.rotation.z = 0;

      // Idle hover animation
      groupRef.current.position.y = startPosition[1] + Math.sin(time * 2) * 0.1;
    } else if (gamePhase === 'crashed') {
      // Crash animation - nose dive
      groupRef.current.rotation.z = THREE.MathUtils.lerp(
        groupRef.current.rotation.z,
        Math.PI / 4, // 45 degree nose down
        0.1
      );
      groupRef.current.position.y = THREE.MathUtils.lerp(
        groupRef.current.position.y,
        -5,
        0.05
      );
    }

    // Update exhaust position
    exhaustPositionRef.current.set(
      groupRef.current.position.x - 1.5,
      groupRef.current.position.y,
      groupRef.current.position.z
    );
  });

  return (
    <group ref={groupRef} position={startPosition}>
      {/* === FUSELAGE === */}
      {/* Main body */}
      <mesh castShadow receiveShadow>
        <capsuleGeometry args={[0.35, 2.5, 8, 16]} />
        <meshStandardMaterial color={PLANE_COLORS.fuselage} roughness={0.3} metalness={0.4} />
      </mesh>

      {/* White accent stripe */}
      <mesh position={[0, 0, 0.36]} castShadow>
        <boxGeometry args={[2.8, 0.08, 0.02]} />
        <meshStandardMaterial color={PLANE_COLORS.stripe} roughness={0.2} metalness={0.1} />
      </mesh>
      <mesh position={[0, 0, -0.36]} castShadow>
        <boxGeometry args={[2.8, 0.08, 0.02]} />
        <meshStandardMaterial color={PLANE_COLORS.stripe} roughness={0.2} metalness={0.1} />
      </mesh>

      {/* === COCKPIT === */}
      <mesh position={[1.0, 0.25, 0]} rotation={[0, 0, -0.2]} castShadow>
        <capsuleGeometry args={[0.25, 0.6, 6, 12]} />
        <meshStandardMaterial
          color={PLANE_COLORS.cockpit}
          roughness={0.1}
          metalness={0.3}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Cockpit frame */}
      <mesh position={[0.8, 0.35, 0]} castShadow>
        <boxGeometry args={[0.6, 0.03, 0.5]} />
        <meshStandardMaterial color={PLANE_COLORS.fuselageAccent} roughness={0.3} metalness={0.5} />
      </mesh>

      {/* === WINGS === */}
      <Wing position={[0, -0.1, 0.8]} scale={[1, 1, 1]} />
      <Wing position={[0, -0.1, -0.8]} scale={[-1, 1, 1]} />

      {/* === TAIL === */}
      <Tail position={[-1.5, 0, 0]} />

      {/* === ENGINE === */}
      <mesh position={[1.4, 0, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.2, 0.25, 0.3, 12]} />
        <meshStandardMaterial color={PLANE_COLORS.engine} roughness={0.3} metalness={0.8} />
      </mesh>

      {/* Engine cowling */}
      <mesh position={[1.55, 0, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.22, 0.2, 0.15, 12]} />
        <meshStandardMaterial color={PLANE_COLORS.fuselageAccent} roughness={0.3} metalness={0.5} />
      </mesh>

      {/* === PROPELLER === */}
      <Propeller position={[1.7, 0, 0]} />

      {/* === LANDING GEAR (simplified) === */}
      {/* Main gear */}
      <mesh position={[0, -0.5, 0.3]} castShadow>
        <cylinderGeometry args={[0.08, 0.08, 0.4, 8]} />
        <meshStandardMaterial color={PLANE_COLORS.engine} roughness={0.4} metalness={0.7} />
      </mesh>
      <mesh position={[0, -0.7, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.05, 12]} />
        <meshStandardMaterial color="#1f2937" roughness={0.8} />
      </mesh>
      <mesh position={[0, -0.5, -0.3]} castShadow>
        <cylinderGeometry args={[0.08, 0.08, 0.4, 8]} />
        <meshStandardMaterial color={PLANE_COLORS.engine} roughness={0.4} metalness={0.7} />
      </mesh>
      <mesh position={[0, -0.7, -0.3]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.05, 12]} />
        <meshStandardMaterial color="#1f2937" roughness={0.8} />
      </mesh>

      {/* Nose gear */}
      <mesh position={[1.2, -0.4, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.05, 0.3, 8]} />
        <meshStandardMaterial color={PLANE_COLORS.engine} roughness={0.4} metalness={0.7} />
      </mesh>
      <mesh position={[1.2, -0.55, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.04, 12]} />
        <meshStandardMaterial color="#1f2937" roughness={0.8} />
      </mesh>

      {/* === EXHAUST TRAIL === */}
      {gamePhase === 'playing' && <ExhaustTrail position={exhaustPositionRef.current} />}
    </group>
  );
}
